// src/pages/Account.jsx
import React, { useEffect, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { updatePassword } from "firebase/auth";
import { auth } from "../firebase";

// Domains
const API_BASE = "https://wixapi.onrender.com";
const MARKETING_BASE = "https://trapound.net";

export default function Account() {
  const [phase, setPhase] = useState("loading"); // "loading" | "ready" | "error"
  const [msg, setMsg] = useState("");

  const [email, setEmail] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [sub, setSub] = useState(null);

  const [opening, setOpening] = useState(false);
  const [copied, setCopied] = useState(false);

  // --- PASSWORD UPDATE STATE ---
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordMsg, setPasswordMsg] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [updatingPassword, setUpdatingPassword] = useState(false);

  const [isEmailUser, setIsEmailUser] = useState(false);

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordMsg("");

    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters.");
      return;
    }

    setUpdatingPassword(true);
    try {
      await updatePassword(auth.currentUser, newPassword);
      setPasswordMsg("Password updated successfully!");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      console.error(err);
      // Firebase security rule: User must have signed in recently to change password
      if (err.code === "auth/requires-recent-login") {
        setPasswordError("For security, please log out and log back in before changing your password.");
      } else {
        setPasswordError(err.message || "Failed to update password.");
      }
    } finally {
      setUpdatingPassword(false);
    }
  };

  const localYMD = (ms, tz='America/New_York') => {
    if (!ms) return '';
    const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year:'numeric', month:'2-digit', day:'2-digit' });
    const p = fmt.formatToParts(new Date(ms));
    const y = p.find(x=>x.type==='year')?.value, m = p.find(x=>x.type==='month')?.value, d = p.find(x=>x.type==='day')?.value;
    return `${y}-${m}-${d}`;
  };
  const displayDate = (str, ms) => ms ? localYMD(ms) : (str || '—');
  const cancelOn = (s) => s?.cancelAtMs || s?.periodEndMs || null;

  useEffect(() => {
    const off = onAuthStateChanged(getAuth(), async (user) => {
      try {
        if (!user) {
          setPhase("error");
          setMsg("You must be signed in to view your account.");
          return;
        }

        const exactEmail = user.email || "";
        setEmail(exactEmail);
        
        const hasPassword = user.providerData.some(p => p.providerId === "password");
        setIsEmailUser(hasPassword);

        // mapping: exact-case first, lowercase fallback
        let mapSnap = await getDoc(doc(db, "stripe_customers_by_email", exactEmail));
        if (!mapSnap.exists()) {
          mapSnap = await getDoc(doc(db, "stripe_customers_by_email", exactEmail.toLowerCase()));
        }
        
        // INSTEAD OF THROWING AN ERROR, JUST SET THE VARIABLES IF THEY EXIST
        if (mapSnap.exists()) {
          const cid = mapSnap.data()?.customerId || "";
          setCustomerId(cid);

          const subSnap = await getDoc(doc(db, "subscribers", cid));
          setSub(subSnap.exists() ? subSnap.data() : null);
        } else {
          // No Stripe record found, but we still want to load the page!
          setCustomerId("");
          setSub(null);
        }

        // Always set the phase to ready so the password and referral cards render
        setPhase("ready");
      } catch (e) {
        console.error("[Account] load error:", e);
        setPhase("error");
        setMsg(e?.message || "Error loading account.");
      }
    });
    return () => off();
  }, []);

  async function openPortal() {
    try {
      setOpening(true);
      const user = getAuth().currentUser;
      if (!user) throw new Error("Not signed in");
      const idToken = await user.getIdToken(true);

      // Secure portal (derives customerId server-side)
      const r = await fetch(`${API_BASE}/portal`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.url) throw new Error(j?.error || `Portal failed (${r.status})`);
      window.location.assign(j.url);
    } catch (e) {
      console.error("[Account] portal error:", e);
      setMsg(e.message);

      // Fallback: simple form post to /create-portal-session if we have customerId
      if (customerId) {
        const form = document.createElement("form");
        form.method = "POST";
        form.action = `${API_BASE}/create-portal-session`;

        const cid = document.createElement("input");
        cid.name = "customerId"; cid.value = customerId; form.appendChild(cid);

        const ret = document.createElement("input");
        ret.name = "returnUrl"; ret.value = window.location.origin + "/account";
        form.appendChild(ret);

        document.body.appendChild(form);
        form.submit();
      } else {
        setOpening(false);
      }
    }
  }

  const pretty = (v) => (v == null || v === "" ? "—" : String(v));

  const badge = (status) => {
    const s = String(status || "").toUpperCase();
    const base = "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold";
    if (s === "ACTIVE")
      return <span className={`${base} bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300`}>Active</span>;
    if (s === "TRIALING")
      return <span className={`${base} bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300`}>Trialing</span>;
    if (s === "PAST_DUE" || s === "PAUSED")
      return <span className={`${base} bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300`}>Past Due</span>;
    if (s === "CANCELED")
      return <span className={`${base} bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300`}>Canceled</span>;
    return <span className={`${base} bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300`}>{s || "Unknown"}</span>;
  };

  // AFTER (tolerant to legacy keys + trims)
  const refCode = (sub?.referralCode ?? sub?.referral_code ?? sub?.referralcode ?? "")
    .toString()
    .trim();

  const referralLink = refCode
    ? `${MARKETING_BASE}/pricing?referredBy=${encodeURIComponent(refCode)}`
    : "";

  async function copyReferral() {
    try {
      if (!referralLink) return;
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_) {}
  }

  return (
    <main className="min-h-screen bg-gray-50 dark:bg-gray-900 relative overflow-hidden lg:ml-64">
      {/* subtle decorative background */}
      <div className="pointer-events-none absolute inset-0 opacity-40 dark:opacity-30">
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full blur-3xl bg-sky-300/40 dark:bg-sky-500/20" />
        <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full blur-3xl bg-indigo-300/40 dark:bg-indigo-500/20" />
      </div>

      <div className="relative max-w-3xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-black tracking-tight text-gray-900 dark:text-gray-100">Account</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage your subscription and referral program.</p>
        </div>

        {/* Loading/Error */}
        {phase === "loading" && (
          <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-sm">
            <div className="animate-pulse h-5 w-40 bg-gray-200 dark:bg-gray-800 rounded mb-4"></div>
            <div className="animate-pulse h-4 w-full bg-gray-200 dark:bg-gray-800 rounded mb-2"></div>
            <div className="animate-pulse h-4 w-2/3 bg-gray-200 dark:bg-gray-800 rounded"></div>
          </div>
        )}
        {phase === "error" && (
          <div className="rounded-2xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950 p-6 text-red-800 dark:text-red-300">
            {msg}
          </div>
        )}

        {phase === "ready" && (
          <>
           {/* Subscription card (single column) */}
            {customerId ? (
              <section className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800 p-6 shadow-sm">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Signed in as</div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{email}</div>
                  </div>
                  {badge(sub?.status)}
                </div>

                <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400">Plan</dt>
                    <dd className="font-medium text-gray-900 dark:text-gray-100">
                      {pretty(sub?.type)}
                    </dd>
                  </div>

                  <div>
                    <dt className="text-gray-500 dark:text-gray-400">Renews</dt>
                    <dd className="font-medium text-gray-900 dark:text-gray-100">
                      {sub?.auto_renews
                        ? "Yes"
                        : cancelOn(sub)
                          ? `No — cancels on ${localYMD(cancelOn(sub))}`
                          : "No"}
                    </dd>
                  </div>

                  <div>
                    <dt className="text-gray-500 dark:text-gray-400">Start Date</dt>
                    <dd className="font-medium text-gray-900 dark:text-gray-100">
                      {displayDate(sub?.startDate, sub?.periodStartMs)}
                    </dd>
                  </div>

                  <div>
                    <dt className="text-gray-500 dark:text-gray-400">End Date</dt>
                    <dd className="font-medium text-gray-900 dark:text-gray-100">
                      {displayDate(sub?.endDate, sub?.periodEndMs)}
                    </dd>
                  </div>

                  <div>
                    <dt className="text-gray-500 dark:text-gray-400">Referral Code</dt>
                    <dd className="font-medium text-gray-900 dark:text-gray-100">
                      {pretty(sub?.referralCode)}
                    </dd>
                  </div>

                  <div>
                    <dt className="text-gray-500 dark:text-gray-400">Referred By</dt>
                    <dd className="font-medium text-gray-900 dark:text-gray-100">
                      {pretty(sub?.referredBy)}
                    </dd>
                  </div>

                  <div className="md:col-span-3">
                    <dt className="text-gray-500 dark:text-gray-400">Stripe Customer ID</dt>
                    <dd className="font-mono text-[12px] text-gray-700 dark:text-gray-300 break-all">
                      {pretty(customerId)}
                    </dd>
                  </div>
                </dl>

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <button
                    onClick={openPortal}
                    disabled={opening}
                    className="inline-flex items-center rounded-xl
                               bg-gray-900 text-white hover:bg-gray-800
                               dark:bg-indigo-600 dark:hover:bg-indigo-500
                               h-11 px-4 font-semibold disabled:opacity-60
                               focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500
                               focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-900"
                  >
                    {opening ? "Opening…" : "Manage billing"}
                  </button>
                </div>

                {sub?.auto_renews === false && String(sub?.status).toUpperCase() !== "CANCELED" && (
                  <div className="mt-4 rounded-xl bg-amber-50 dark:bg-amber-900/30
                                  text-amber-800 dark:text-amber-200 p-3">
                    Auto‑renew is off. Your access remains active until {localYMD(cancelOn(sub))}.
                    You can resume billing anytime in the portal.
                  </div>
                )}
              </section>
            ) : (
              <section className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800 p-8 shadow-sm text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 dark:bg-gray-700 mb-4">
                  <svg className="h-6 w-6 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">No Active Billing</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">There is no billing account currently linked to <b>{email}</b>.</p>
              </section>
            )}


            {/* --- CHANGE PASSWORD SECTION (Email Users Only) --- */}
      {isEmailUser && (
        <div className="mt-8 p-6 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
            Change Password
          </h3>
          <form onSubmit={handleUpdatePassword} className="max-w-md space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-2 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-500 outline-none text-gray-900 dark:text-white"
                placeholder="At least 6 characters"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-2 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-500 outline-none text-gray-900 dark:text-white"
                placeholder="Confirm your new password"
              />
            </div>

            {passwordError && (
              <div className="text-red-500 text-sm font-semibold">{passwordError}</div>
            )}
            {passwordMsg && (
              <div className="text-green-500 text-sm font-semibold">{passwordMsg}</div>
            )}

            <button
              type="submit"
              disabled={updatingPassword || !newPassword || !confirmPassword}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl disabled:opacity-50 transition"
            >
              {updatingPassword ? "Updating..." : "Update Password"}
            </button>
          </form>
        </div>
      )}

            {/* Referral card (single column) */}
            <section className="mt-6 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800 p-6 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="shrink-0 h-10 w-10 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-600 text-white flex items-center justify-center shadow">
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 12v7a2 2 0 0 1-2 2h-3M4 12v7a2 2 0 0 0 2 2h3M2 9h20M12 22V9" />
                    <path d="M12 9c1.657 0 3-1.57 3-3.5S13.657 2 12 2 9 3.57 9 5.5 10.343 9 12 9Zm0 0h7c.943 0 1.706-.764 1.706-1.706 0-.943-.763-1.706-1.706-1.706H15" />
                    <path d="M12 9H5c-.943 0-1.706-.764-1.706-1.706C3.294 6.35 4.057 5.587 5 5.587h4" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Sharing is Caring!</h2>
                  <p className="mt-1 text-gray-700 dark:text-gray-300 leading-relaxed">
                    Earn cash when you refer someone and they subscribe.
                    You’ll receive a one-time bonus after their free trial ends and the first payment is processed.
                  </p>
                </div>
              </div>

              <ul className="mt-4 space-y-2 text-gray-700 dark:text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="mt-1 h-2 w-2 rounded-full bg-sky-500" />
                  <span><strong className="text-gray-900 dark:text-gray-100">$20</strong> one-time bonus for an <strong className="text-gray-900 dark:text-gray-100">annual</strong> subscription.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 h-2 w-2 rounded-full bg-indigo-500" />
                  <span><strong className="text-gray-900 dark:text-gray-100">$10</strong> one-time bonus for a <strong className="text-gray-900 dark:text-gray-100">monthly</strong> subscription.</span>
                </li>
              </ul>

              <div className="mt-5">
                <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">
                  Your referral link
                </label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="text"
                    readOnly
                    value={referralLink || "Your referral link will appear here once available"}
                    className="w-full rounded-xl border border-gray-300 dark:border-gray-700
                               bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                               px-3 py-2 text-sm font-mono"
                  />
                  <button
                    onClick={copyReferral}
                    disabled={!referralLink}
                    className="inline-flex items-center justify-center rounded-xl
                               border border-gray-300 dark:border-gray-700
                               h-11 px-4 text-sm font-semibold
                               text-gray-700 dark:text-gray-200
                               hover:bg-gray-50 dark:hover:bg-gray-800
                               disabled:opacity-60
                               focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500
                               focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-900"
                    title={referralLink || "No referral link yet"}
                  >
                    {copied ? "Copied!" : "Copy"}
                  </button>
                </div>

                <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                  Share this link with friends. When they subscribe, your bonus is tracked automatically.
                </p>
              </div>
            </section>
          </>
        )}
      </div>
    </main>
  );
}
